import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  ButtonGroup,
  FormControlLabel,
  Switch,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { toast } from "react-toastify";
import APIHelper from "./utils/APIHelper";
import ButtonTemplatePreview from "./ButtonTemplatePreview";
import { Close as CloseIcon } from "@mui/icons-material";
import { trackEvent } from "../analytics";
import { useNavigate } from "react-router-dom";

const ButtonTemplateConfig = ({
  token,
  postId,
  pageId,
  onClose,
  // Controlled mode props (for scheduler)
  value,
  onChange,
  controlled = false,
  caption = "", // For AI keyword suggestions in scheduler
}) => {
  const navigate = useNavigate();
  // Use controlled values if provided, otherwise use internal state
  const [internalKeywords, setInternalKeywords] = useState([]);
  const [internalIsEnabled, setInternalIsEnabled] = useState(false);
  const [internalTemplates, setInternalTemplates] = useState([
    {
      headline: "",
      description: "",
      buttons: [{ name: "", url: "" }],
      imageUrl: "",
    },
  ]);

  const keywords = controlled ? value?.commentKeywords || [] : internalKeywords;
  const isEnabled = controlled ? value?.isEnabled || false : internalIsEnabled;
  const defaultTemplateArray = [
    {
      headline: "",
      description: "",
      buttons: [{ name: "", url: "" }],
      imageUrl: "",
    },
  ];
  const templates = controlled
    ? value?.cardTemplates &&
      Array.isArray(value.cardTemplates) &&
      value.cardTemplates.length > 0
      ? value.cardTemplates
      : defaultTemplateArray
    : internalTemplates && internalTemplates.length > 0
    ? internalTemplates
    : defaultTemplateArray;

  const [commentToCheck, setCommentToCheck] = useState("");
  const [cardTemplates, setCardTemplates] = useState(
    templates && templates.length > 0 ? templates : defaultTemplateArray
  );
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceInput, setPriceInput] = useState("");
  const [currentButtonIndex, setCurrentButtonIndex] = useState(null);
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState("");
  const [products, setProducts] = useState([]);

  const openProductDialog = async () => {
    setProductDialogOpen(true);
    setProductLoading(true);
    setProductError("");
    try {
      const res = await APIHelper.listProducts(token, {
        status: "Active",
        page: 1,
        limit: 50,
        sortBy: "title",
        sortDir: "asc",
      });
      const items = res.items || [];
      if (!items.length) {
        // No products – send user to create product page
        setProductDialogOpen(false);
        navigate("/dashboard/products");
        return;
      }
      setProducts(items);
    } catch (error) {
      console.error("Error loading products for selector:", error);
      setProductError("Failed to load products. Please try again.");
    } finally {
      setProductLoading(false);
    }
  };

  const handleSelectProduct = async (product) => {
    if (!product) return;
    try {
      // 1. Update template content from product
      const updatedTemplates = [...templates];
      const tpl = {
        ...updatedTemplates[activeTemplateIndex],
        headline: product.title || "",
        description: product.description || "",
        imageUrl:
          product.thumbnailUrl ||
          updatedTemplates[activeTemplateIndex].imageUrl ||
          "",
      };

      // Ensure at least one button exists
      const buttons =
        tpl.buttons && tpl.buttons.length
          ? [...tpl.buttons]
          : [{ name: "", url: "" }];

      // 2. Create/refresh checkout for first button using product price
      const price = product.price;
      if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
        toast.error(
          "Product does not have a valid price. Please update the product price first."
        );
        return;
      }

      if (controlled) {
        buttons[0] = {
          ...(buttons[0] || {}),
          name: "Buy Now",
          amount: Math.round(price * 100),
          pendingPaymentLink: true,
          pendingAmount: Math.round(price * 100),
          pendingProductName: product.title || "Product",
          productSku: product.sku || "",
        };

        tpl.buttons = buttons;
        updatedTemplates[activeTemplateIndex] = tpl;

        updateValue({ cardTemplates: updatedTemplates });
        toast.success(
          "Product applied to button template. Checkout URL will be created automatically after publication."
        );
        setProductDialogOpen(false);
        return;
      }

      setCreatingPaymentLink(true);
      const response = await APIHelper.createProductCheckout(token, {
        postId,
        buttonIndex: 0,
        amount: price,
        productName: product.title || "Product",
        pageId,
        productSku: product.sku,
      });

      if (!response.success || !response.checkoutUrl) {
        throw new Error(response.error || "Failed to create checkout link");
      }

      buttons[0] = {
        ...(buttons[0] || {}),
        name: "Buy Now",
        url: response.checkoutUrl,
        productId: response.productId,
        productName: response.productName,
        amount: response.amount,
        productSku: product.sku,
      };

      tpl.buttons = buttons;
      updatedTemplates[activeTemplateIndex] = tpl;

      setInternalTemplates(updatedTemplates);
      setCardTemplates(updatedTemplates);

      toast.success("Product applied to button template");
      setProductDialogOpen(false);
    } catch (error) {
      console.error("Error applying product to template:", error);
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to apply product. Please try again."
      );
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const updateValue = (updates) => {
    if (controlled && onChange) {
      onChange({
        ...value,
        ...updates,
        templateType: "ButtonTemplate",
      });
    }
  };

  useEffect(() => {
    if (controlled) {
      // In controlled mode, sync templates from value
      // Ensure we always have at least one template
      if (
        value?.cardTemplates &&
        Array.isArray(value.cardTemplates) &&
        value.cardTemplates.length > 0
      ) {
        setCardTemplates(value.cardTemplates);
        setInternalTemplates(value.cardTemplates);
      } else if (
        !value?.cardTemplates ||
        (Array.isArray(value?.cardTemplates) &&
          value.cardTemplates.length === 0)
      ) {
        // Initialize with default template if empty or undefined
        setCardTemplates(defaultTemplateArray);
        setInternalTemplates(defaultTemplateArray);
        // Only update parent if value doesn't have cardTemplates at all (to avoid infinite loop)
        if (onChange && (!value || !value.cardTemplates)) {
          onChange({
            ...(value || {}),
            cardTemplates: defaultTemplateArray,
            templateType: "ButtonTemplate",
          });
        }
      }
    } else {
      // In standalone mode, sync from internal state
      if (Array.isArray(cardTemplates) && cardTemplates.length > 0) {
        setInternalTemplates(cardTemplates);
      } else if (!cardTemplates || cardTemplates.length === 0) {
        setInternalTemplates(defaultTemplateArray);
        setCardTemplates(defaultTemplateArray);
      }
    }
  }, [controlled, value?.cardTemplates, cardTemplates]);

  useEffect(() => {
    // Only fetch if not in controlled mode and postId exists
    if (controlled || !postId) return;

    const fetchTemplate = async () => {
      try {
        const config = await APIHelper.fetchMediaAutoCommentConfig(
          token,
          postId
        );

        if (config) {
          setInternalKeywords(config.commentKeywords || []);
          // Ensure cardTemplates is an array
          // Handle both new (array) and old (potentially single object or other formats) configurations
          let templates = defaultTemplateArray;
          if (config.cardTemplates) {
            if (Array.isArray(config.cardTemplates)) {
              // Normal case: already an array (works for both old and new configs)
              templates =
                config.cardTemplates.length > 0
                  ? config.cardTemplates
                  : defaultTemplateArray;
            } else if (typeof config.cardTemplates === "object") {
              // Edge case: single object (shouldn't happen with current schema, but handle it)
              templates = [config.cardTemplates];
            }
          }
          setCardTemplates(templates);
          setInternalTemplates(templates);
          setInternalIsEnabled(config.isEnabled || false);
        } else {
          // If no config found, initialize with defaults
          setCardTemplates(defaultTemplateArray);
          setInternalTemplates(defaultTemplateArray);
        }
      } catch (error) {
        console.error("Failed to load configuration:", error);
        toast.error("Failed to load configuration");
        // Initialize with defaults on error
        setCardTemplates(defaultTemplateArray);
        setInternalTemplates(defaultTemplateArray);
      }
    };

    fetchTemplate();
  }, [token, postId, controlled]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const response = await APIHelper.uploadImageToCloudinary(file);
      const uploadedImageUrl = response?.secure_url;

      if (uploadedImageUrl) {
        const updatedTemplates = [...templates];
        updatedTemplates[activeTemplateIndex].imageUrl = uploadedImageUrl;
        if (controlled) {
          updateValue({ cardTemplates: updatedTemplates });
        } else {
          setInternalTemplates(updatedTemplates);
          setCardTemplates(updatedTemplates);
        }
      }
    } catch (error) {
      toast.error("Failed to upload image. Please try again.");
    }

    e.target.value = ""; // 🔥 Reset input to allow re-uploading the same image
  };

  const handleRemoveImage = () => {
    const updatedTemplates = [...templates];
    updatedTemplates[activeTemplateIndex].imageUrl = "";
    if (controlled) {
      updateValue({ cardTemplates: updatedTemplates });
    } else {
      setInternalTemplates(updatedTemplates);
      setCardTemplates(updatedTemplates);
    }
  };
  // Example of updating the template inside ButtonTemplateConfig
  const handleTemplateChange = (field, newValue) => {
    const updatedTemplates = templates.map((template, index) =>
      index === activeTemplateIndex
        ? { ...template, [field]: newValue }
        : template
    );

    if (controlled) {
      updateValue({ cardTemplates: updatedTemplates });
    } else {
      setInternalTemplates(updatedTemplates);
      setCardTemplates(updatedTemplates);
    }
  };

  const handleAddButton = () => {
    const updatedTemplates = [...templates];
    if (updatedTemplates[activeTemplateIndex].buttons.length < 3) {
      updatedTemplates[activeTemplateIndex].buttons.push({
        name: "",
        url: "",
      });
      if (controlled) {
        updateValue({ cardTemplates: updatedTemplates });
      } else {
        setInternalTemplates(updatedTemplates);
        setCardTemplates(updatedTemplates);
      }
    } else {
      toast.error("You can only add up to 3 buttons per template.");
    }
  };

  const handleDeleteButton = (buttonIndex) => {
    const updatedTemplates = [...templates];
    const buttons = updatedTemplates[activeTemplateIndex].buttons;
    if (buttons.length > 1) {
      buttons.splice(buttonIndex, 1);
      if (controlled) {
        updateValue({ cardTemplates: updatedTemplates });
      } else {
        setInternalTemplates(updatedTemplates);
        setCardTemplates(updatedTemplates);
      }
    } else {
      toast.error("At least one button is required.");
    }
  };

  const handleButtonChange = (buttonIndex, field, newValue) => {
    const updatedTemplates = [...templates];
    updatedTemplates[activeTemplateIndex].buttons[buttonIndex] = {
      ...updatedTemplates[activeTemplateIndex].buttons[buttonIndex],
      [field]: newValue,
    };
    if (controlled) {
      updateValue({ cardTemplates: updatedTemplates });
    } else {
      setInternalTemplates(updatedTemplates);
      setCardTemplates(updatedTemplates);
    }
  };

  const handleAddTemplate = () => {
    const updatedTemplates = [
      ...templates,
      {
        headline: "",
        description: "",
        buttons: [{ name: "", url: "" }],
        imageUrl: "",
      },
    ];
    if (controlled) {
      updateValue({ cardTemplates: updatedTemplates });
    } else {
      setInternalTemplates(updatedTemplates);
      setCardTemplates(updatedTemplates);
    }
    setActiveTemplateIndex((prev) => prev + 1);
  };

  const handleRemoveTemplate = () => {
    if (templates.length > 1) {
      const updatedTemplates = templates.slice(0, -1);
      if (controlled) {
        updateValue({ cardTemplates: updatedTemplates });
      } else {
        setInternalTemplates(updatedTemplates);
        setCardTemplates(updatedTemplates);
      }
      setActiveTemplateIndex((prev) => Math.max(0, prev - 1));
    } else {
      toast.error("At least one template is required.");
    }
  };

  const handleAddKeyword = () => {
    if (commentToCheck.trim() && !keywords.includes(commentToCheck.trim())) {
      const newKeywords = [...keywords, commentToCheck.trim()];
      if (controlled) {
        updateValue({ commentKeywords: newKeywords });
      } else {
        setInternalKeywords(newKeywords);
      }
      setCommentToCheck("");
    }
  };

  const handleDeleteKeyword = (keywordToDelete) => {
    const newKeywords = keywords.filter(
      (keyword) => keyword !== keywordToDelete
    );
    if (controlled) {
      updateValue({ commentKeywords: newKeywords });
    } else {
      setInternalKeywords(newKeywords);
    }
  };

  const handleMagicWandClick = (buttonIndex) => {
    setCurrentButtonIndex(buttonIndex);
    setPriceInput("");
    setPriceDialogOpen(true);
  };

  const handlePriceDialogClose = () => {
    setPriceDialogOpen(false);
    setPriceInput("");
    setCurrentButtonIndex(null);
  };

  const handlePriceSubmit = async () => {
    if (
      !priceInput ||
      isNaN(parseFloat(priceInput)) ||
      parseFloat(priceInput) <= 0
    ) {
      toast.error("Please enter a valid price");
      return;
    }

    const price = parseFloat(priceInput);
    const amountInPaise = Math.round(price * 100);
    const productName =
      templates[activeTemplateIndex].headline ||
      templates[activeTemplateIndex].description ||
      "Product";

    // In controlled mode (scheduler), store the amount for later checkout URL creation
    if (controlled) {
      const updatedTemplates = [...templates];
      updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex] = {
        ...updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex],
        name:
          updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex]
            .name || "Buy Now",
        amount: amountInPaise,
        // Store pending checkout info (will be created after post is published)
        pendingPaymentLink: true,
        pendingCheckout: true,
        pendingAmount: amountInPaise,
        pendingProductName: productName,
      };
      updateValue({ cardTemplates: updatedTemplates });
      toast.success(
        "Price set. Checkout URL will be created automatically after the post is published."
      );
      handlePriceDialogClose();
      return;
    }

    // In standalone mode, create product checkout URL immediately
    if (!postId) {
      toast.error(
        "Post ID is required. Please configure checkout from a specific post, not from page-level settings."
      );
      handlePriceDialogClose();
      return;
    }

    if (!pageId) {
      toast.error("Page ID is required to create checkout URL");
      handlePriceDialogClose();
      return;
    }

    try {
      setCreatingPaymentLink(true);

      // Create product checkout URL (replaces payment link)
      const response = await APIHelper.createProductCheckout(token, {
        postId: postId,
        buttonIndex: currentButtonIndex,
        amount: price,
        productName: productName,
        pageId: pageId,
      });

      if (response.success && response.checkoutUrl) {
        // Update the button with checkout URL
        const updatedTemplates = [...templates];
        updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex] = {
          ...updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex],
          name: "Buy Now",
          url: response.checkoutUrl,
          productId: response.productId,
          productName: response.productName,
          amount: response.amount,
          productSku:
            updatedTemplates[activeTemplateIndex].buttons[currentButtonIndex]
              ?.productSku || null,
        };
        setInternalTemplates(updatedTemplates);
        setCardTemplates(updatedTemplates);

        toast.success("Checkout URL created successfully");
      } else {
        throw new Error("Failed to create payment link");
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
      toast.error(
        error.response?.data?.error ||
          "Failed to create payment link. Please try again."
      );
    } finally {
      setCreatingPaymentLink(false);
      handlePriceDialogClose();
    }
  };

  const handleSuggestKeywords = async () => {
    try {
      setSuggestingKeywords(true);

      // Get context: in controlled mode use caption, otherwise use template description/headline
      const context = controlled
        ? caption || "Instagram post"
        : templates[activeTemplateIndex].description ||
          templates[activeTemplateIndex].headline ||
          "";

      const suggestedKeywords = await APIHelper.suggestKeywords(token, {
        context: context || "Instagram post",
        postId: postId || null,
      });

      if (suggestedKeywords && suggestedKeywords.length > 0) {
        // Add all suggested keywords that aren't already in the list
        const newKeywords = suggestedKeywords.filter(
          (kw) => kw && !keywords.includes(kw.toLowerCase().trim())
        );
        if (newKeywords.length > 0) {
          const updatedKeywords = [...keywords, ...newKeywords];
          if (controlled) {
            updateValue({ commentKeywords: updatedKeywords });
          } else {
            setInternalKeywords(updatedKeywords);
          }
          toast.success(`Added ${newKeywords.length} suggested keywords`);
        } else {
          toast.info("All suggested keywords are already added");
        }
      } else {
        toast.warning(
          "No keywords suggested. Try adding more context in description."
        );
      }
    } catch (error) {
      console.error("Error suggesting keywords:", error);
      toast.error("Failed to suggest keywords. Please try again.");
    } finally {
      setSuggestingKeywords(false);
    }
  };

  const handleSave = async () => {
    try {
      // Use templates (which is the source of truth) instead of cardTemplates
      const config = {
        commentKeywords: keywords,
        cardTemplates: templates, // Use templates which is computed from the correct source
        isEnabled,
        templateType: "ButtonTemplate",
      };
      if (postId) {
        await APIHelper.updateMediaAutoCommentConfig(token, postId, config);
      } else {
        await APIHelper.updateAutoCommentConfig(token, config);
      }
      console.log(config);
      toast.success("Configuration saved successfully");
      trackEvent("Button Template Configured", { postId });
      onClose();
    } catch (error) {
      toast.error("Failed to save configuration");
      trackEvent("Button Template Config Error", {
        postId,
        error: error.message,
      });
    }
  };

  // Safety check: ensure templates has at least one element
  const safeTemplates =
    templates && templates.length > 0 ? templates : defaultTemplateArray;
  const safeActiveIndex =
    activeTemplateIndex >= safeTemplates.length ? 0 : activeTemplateIndex;
  const currentTemplate =
    safeTemplates[safeActiveIndex] || defaultTemplateArray[0];

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Box
        sx={{
          flex: 2,
          p: 2,
        }}
      >
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.light",
            backgroundColor: "primary.lightest",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Selling a product?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Save time and avoid mistakes by picking a product from your
            inventory. We’ll auto-fill the card content and create a checkout
            link for you.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={openProductDialog}
            disabled={!token || (!controlled && !postId) || !pageId}
          >
            Choose Product from Inventory
          </Button>
        </Box>

        {/* Text fields */}
        <TextField
          fullWidth
          label="Headline"
          value={currentTemplate.headline || ""}
          onChange={(e) => handleTemplateChange("headline", e.target.value)}
          variant="outlined"
          margin="normal"
        />
        <TextField
          fullWidth
          label="Description"
          value={currentTemplate.description || ""}
          onChange={(e) => handleTemplateChange("description", e.target.value)}
          variant="outlined"
          margin="normal"
          multiline
          rows={3}
        />

        {(currentTemplate.buttons || []).map((button, buttonIndex) => (
          <Box
            key={buttonIndex}
            display="flex"
            alignItems="center"
            gap={1}
            mt={1}
            mb={1}
          >
            <TextField
              label="Button URL"
              value={button.url}
              onChange={(e) =>
                handleButtonChange(buttonIndex, "url", e.target.value)
              }
              variant="outlined"
              fullWidth
            />
            <TextField
              label="Button Name"
              value={button.name}
              onChange={(e) =>
                handleButtonChange(buttonIndex, "name", e.target.value)
              }
              variant="outlined"
              fullWidth
            />
            <Tooltip
              title={
                !postId || !pageId
                  ? "Post ID and Page ID are required to create payment links"
                  : "Use our payment link to fulfill"
              }
            >
              <span>
                <IconButton
                  color="primary"
                  onClick={() => handleMagicWandClick(buttonIndex)}
                  disabled={!postId || !pageId}
                >
                  <AutoAwesomeIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              color="error"
              onClick={() => handleDeleteButton(buttonIndex)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Button
          variant="contained"
          onClick={handleAddButton}
          disabled={templates[activeTemplateIndex].buttons.length >= 3}
        >
          Add Additional Button
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <TextField
            fullWidth
            label="Add Comment Keywords (Press Enter to add)"
            value={commentToCheck}
            onChange={(e) => setCommentToCheck(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddKeyword();
              }
            }}
            placeholder="Type a keyword and press Enter"
            variant="outlined"
            margin="normal"
          />
          <Tooltip title="AI Suggest Keywords">
            <span>
              <IconButton
                color="primary"
                onClick={handleSuggestKeywords}
                disabled={suggestingKeywords}
                sx={{ mt: 1 }}
              >
                {suggestingKeywords ? (
                  <CircularProgress size={20} />
                ) : (
                  <AutoAwesomeIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {keywords.map((keyword, index) => (
            <Chip
              key={index}
              label={keyword}
              onDelete={() => handleDeleteKeyword(keyword)}
              color="primary"
            />
          ))}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              onChange={(e) => {
                if (controlled) {
                  updateValue({ isEnabled: e.target.checked });
                } else {
                  setInternalIsEnabled(e.target.checked);
                }
              }}
            />
          }
          label="Enable Auto DM"
        />
        {!controlled && (
          <Box sx={{ width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleSave}
            >
              Save Template
            </Button>
          </Box>
        )}
      </Box>

      {/* Carousel Slider and Image Upload - Always show, but hide preview in controlled mode */}
      <Box sx={{ flex: 1, ml: 10 }}>
        <Typography variant="subtitle1" mb={1}>
          Carousel Slider
        </Typography>
        <ButtonGroup variant="outlined">
          {templates.map((_, index) => (
            <Button key={index} onClick={() => setActiveTemplateIndex(index)}>
              {index + 1}
            </Button>
          ))}
          <Button onClick={handleAddTemplate}>+</Button>
          {templates.length > 1 && (
            <Button onClick={handleRemoveTemplate}>x</Button>
          )}
        </ButtonGroup>

        <Box mt={2}>
          <Typography variant="subtitle1">Image</Typography>
          <Button variant="contained" component="label">
            Upload
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
          </Button>

          {templates[activeTemplateIndex].imageUrl && (
            <Box
              sx={{
                position: "relative",
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                overflow: "visible",
                border: "1px solid gray",
                mt: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={templates[activeTemplateIndex].imageUrl}
                alt="Uploaded"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  backgroundColor: "white",
                  color: "black",
                  width: "16px",
                  height: "16px",
                  p: 0,
                  minWidth: "unset",
                  boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
                onClick={handleRemoveImage}
              >
                <CloseIcon sx={{ fontSize: "12px" }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Preview - Hide in controlled mode (scheduler has its own preview) */}
        {!controlled && (
          <Box sx={{ mt: 2 }}>
            <ButtonTemplatePreview
              key={JSON.stringify(templates)}
              cardTemplates={templates}
              activeTemplateIndex={activeTemplateIndex}
            />
          </Box>
        )}
      </Box>

      {/* Price Input Dialog */}
      <Dialog open={priceDialogOpen} onClose={handlePriceDialogClose}>
        <DialogTitle>Configure Payment Link</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the price for this payment link. The button will be updated to
            "Pay" with a Razorpay payment link.
            {(!postId || !pageId) && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning">
                  Post ID and Page ID are required. Please configure this from a
                  specific post.
                </Alert>
              </Box>
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Price (INR)"
            type="number"
            fullWidth
            variant="outlined"
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            placeholder="Enter amount"
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlePriceDialogClose}
            disabled={creatingPaymentLink}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePriceSubmit}
            variant="contained"
            color="primary"
            disabled={creatingPaymentLink}
          >
            {creatingPaymentLink ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              "Apply"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Selector Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select a product</DialogTitle>
        <DialogContent dividers>
          {productError && (
            <Box mb={2}>
              <Alert severity="error" onClose={() => setProductError("")}>
                {productError}
              </Alert>
            </Box>
          )}
          {productLoading ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {products.map((p) => (
                <Box
                  key={p._id || p.sku}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => handleSelectProduct(p)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {p.thumbnailUrl && (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <img
                          src={p.thumbnailUrl}
                          alt={p.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2">
                        {p.title}{" "}
                        {p.sku && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            • {p.sku}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ₹{Number(p.price || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {!products.length && !productLoading && !productError && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  No products found. You can add products from the Products
                  page.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setProductDialogOpen(false);
              navigate("/dashboard/products");
            }}
          >
            Go to Products
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ButtonTemplateConfig;
